import { type JSX, type ReactNode, type RefObject, useLayoutEffect, useRef } from 'react';
import { cx } from './primitives';

/**
 * The frame around a HAND-WRITTEN table: the scroll container, its accessible
 * name, and - below `md` - the card labels that make each row readable on a
 * phone.
 *
 * `ResponsiveTable` (data.tsx) already does all of this for a table declared as
 * column objects, because it holds the header and the cell together and can put
 * one on the other's `data-label`. Most screens in the two staff portals do not
 * declare their tables that way: they pass `<thead>`/`<tbody>` as JSX, which is
 * the right shape for a row that carries a badge, a menu and a checkbox, and it
 * is why those screens got the sideways-scrolling mode and nothing else. On a
 * 390px phone that is a seven-column ledger panned one column at a time, with
 * the column names off-screen the moment you pan - which is what the partner
 * Orders and Apartments screens actually did.
 *
 * So the label is DERIVED from the header cell in the same column, at commit
 * time, rather than restated on every `<td>`. Restating it is the failure this
 * codebase already knows by name: the same fact written at two call sites, drift
 * between them, and a card whose label says one thing while the column it came
 * from says another. Here they cannot disagree - there is one string and the
 * card reads it off the header.
 *
 * A header cell opts its column out with `data-card-label=""` (a checkbox
 * column, a row of buttons: "Actions" stamped in front of a menu trigger reads
 * worse than nothing), or overrides the text with `data-card-label="..."` when
 * the visible heading is not what a card should say.
 */

// ---------------------------------------------------------------------------
// The mapping, pure
// ---------------------------------------------------------------------------

/**
 * The card label for each cell of one row, given the table's per-column labels.
 *
 * Cells are matched to columns by position and not by index, because a cell can
 * span several - a "no rows yet" line, a totals row. A spanning cell belongs to
 * no single column and therefore gets no label; it renders as a full-width block
 * inside the card, which is what such a cell is.
 */
export function cardLabelsFor(
  headers: readonly (string | undefined)[],
  spans: readonly number[],
): (string | undefined)[] {
  const labels: (string | undefined)[] = [];
  let column = 0;
  for (const span of spans) {
    labels.push(span === 1 ? headers[column] : undefined);
    column += span;
  }
  return labels;
}

/** The label a header cell contributes, or undefined when the column opts out. */
export function headerCardLabel(cell: {
  readonly dataset: DOMStringMap;
  readonly textContent: string | null;
}): string | undefined {
  const declared = cell.dataset.cardLabel;
  const text = (declared ?? cell.textContent ?? '').replace(/\s+/gu, ' ').trim();
  return text === '' ? undefined : text;
}

// ---------------------------------------------------------------------------
// The effect
// ---------------------------------------------------------------------------

/**
 * Mirrors the header row onto every body and footer cell as `data-label`.
 *
 * Deliberately no dependency array. The labels are a function of the DOM the
 * last commit produced, and these tables re-render on every fetch, every sort
 * and every page step; a dependency list would have to name all of that and
 * would silently be wrong the first time a screen added a column. The work is
 * one pass over the cells of at most one page of rows.
 *
 * `useLayoutEffect` rather than `useEffect`: the attribute is what the card
 * layout draws its labels from, so it has to be set before paint or the first
 * frame after every fetch shows a phone a column of unlabelled values.
 *
 * Writing an attribute React did not render is safe here precisely because no
 * call site passes `data-label` itself - React never reconciles an attribute it
 * has not seen, so nothing overwrites this on the next commit.
 */
function useCardLabels(ref: RefObject<HTMLTableElement | null>): void {
  useLayoutEffect(() => {
    const table = ref.current;
    if (table === null) return;
    applyTableChrome(table);

    /*
     * ...and again whenever the box the table is drawn in changes size.
     *
     * The pass above runs on render, and a window the operator drags narrower
     * renders nothing: React has no reason to. Without this the fit is decided
     * once, at whatever width the table happened to be born at, and the rule
     * quietly stops holding the moment somebody collapses the sidebar - which
     * is the one gesture on these screens that changes a table's width by 192px.
     */
    const box = table.parentElement;
    if (box === null) return;
    /*
     * Guarded on the width it last fitted against, and the guard is not an
     * optimisation - without it this is a loop that freezes the tab. Fitting a
     * table changes the table's layout, which changes the height of the box,
     * which takes a vertical scrollbar away and hands the box back ~15px of
     * width, which notifies this observer, which fits the table. The only
     * stable question is "is this a width I have not already fitted against".
     *
     * WHICH IS THE WIDTH THE TABLE ENDED AT, not the one that woke us up. The
     * guard recorded the incoming width and then ran the pass, which is a
     * two-cycle rather than a fixed point whenever the pass MOVES the width: a
     * table that cannot seat is released to its full natural, the box gains a
     * horizontal scrollbar and loses its vertical one, and comes back ~15px
     * wider - a width the guard has not seen, so it fits again, which restores
     * the scrollbar and the original width, which it has also not seen since.
     * Recording what the box actually reads afterwards settles that on the first
     * turn. It costs one more layout read, on a callback that has just forced
     * several.
     */
    // jsdom has no ResizeObserver, and the `*.test.tsx` lane mounts these
    // tables by the dozen. Nothing is lost by its absence: a DOM with no layout
    // has no width to react to, and the pass above has already run.
    if (typeof ResizeObserver === 'undefined') return;
    let fittedAt = box.clientWidth;
    const observer = new ResizeObserver(() => {
      const width = box.clientWidth;
      if (width === fittedAt) return;
      applyTableChrome(table);
      fittedAt = box.clientWidth;
    });
    observer.observe(box);
    return () => {
      observer.disconnect();
    };
  });
}

/**
 * Everything this frame does to the DOM the last commit produced, in the one
 * order the three passes depend on: the card labels first, then the fit, then
 * the titles - a cell is only clipped once the columns have been decided.
 */
function applyTableChrome(table: HTMLTableElement): void {
  {
    const head = table.tHead;
    if (head === null) return;
    // The LAST header row: a grouped header puts the column names on the second
    // one, and the group titles above them are not what a card wants to say.
    const headRow = head.rows[head.rows.length - 1];
    if (headRow === undefined) return;

    const headers: (string | undefined)[] = [];
    for (const cell of headRow.cells) {
      const label = headerCardLabel(cell);
      for (let index = 0; index < Math.max(1, cell.colSpan); index += 1) headers.push(label);
    }

    const rows = [
      ...Array.from(table.tBodies).flatMap((body) => Array.from(body.rows)),
      ...(table.tFoot === null ? [] : Array.from(table.tFoot.rows)),
    ];

    for (const row of rows) {
      const cells = Array.from(row.cells);
      const labels = cardLabelsFor(
        headers,
        cells.map((cell) => Math.max(1, cell.colSpan)),
      );
      cells.forEach((cell, index) => {
        const label = labels[index];
        if (label === undefined) cell.removeAttribute('data-label');
        else cell.setAttribute('data-label', label);
      });
    }

    // The labels are all written by here, so the reads below happen in one
    // pass rather than interleaved with writes - a read after a write is what
    // makes a per-cell loop thrash layout.
    fitColumns(table, headers.length);
    // The header row comes too: on a dense table the column NAMES are what
    // clip first, and a heading cut to "ADJUSTM..." with no way to read the
    // rest is the one clip an operator cannot guess their way past.
    titleClippedCells([
      ...Array.from(headRow.cells),
      ...rows.flatMap((row) => Array.from(row.cells)),
    ]);
  }
}

/** The colgroup this file owns, so a table that authored its own keeps it. */
const FIT_MARK = 'data-cib-fit';

/**
 * THE LAST-RESORT FLOOR, and nothing else.
 *
 * A column narrower than this says nothing whatever is in it, so no arithmetic
 * here may go under it. What it is NOT is the width a column ought to get: that
 * is `FitColumn.least`, which is derived from the column's own heading, and this
 * is only what a column with no heading to speak of falls back to.
 */
const MIN_COLUMN_PX = 56;

/**
 * How many characters of a heading have to survive for it to still NAME its
 * column.
 *
 * Five, which is a judgement rather than a measurement, and the reasoning is the
 * one `releaseHeadingAir` already states: a heading may be shortened, it may not
 * stop naming its column. `Order` and `Actor` cut to `O` and `A` beside their
 * sort marks are not shortened headings, they are two unlabelled columns of
 * opaque values; `Adjust...` is a shortened heading and a reader gets there.
 * Five is where the platform's own column names stop being guessable - `When`,
 * `What`, `Where`, `Actor`, `Order`, `Payee` are four and five letters, so
 * anything shorter is guessing between them.
 */
const NAMED_CHARS = 5;

/**
 * What one column may never be seated below: enough for its heading to stay a
 * word, and never more than the column asked for in the first place.
 *
 * The cap is what makes the two rules compose. `releaseHeadingAir` above files
 * a HELD column's `natural` down toward the width of its own figures - it takes
 * heading air away ON PURPOSE - so a heading minimum that then clawed those
 * pixels back would leave the two rules arguing over the same column, and the
 * one stated in pixels would win. A column is never handed more than its
 * (possibly filed-down) natural, in either direction, which is a promise this
 * fitter has kept since it was written.
 */
function leastOf(column: FitColumn): number {
  return Math.max(MIN_COLUMN_PX, Math.min(column.least ?? 0, column.natural));
}

/**
 * How much of its width a column is entitled to keep, as layout.css declares it
 * on `--cib-cell-whole`.
 *
 *  - `hard` (`1`): money, a date, a control. Never squeezed, at any price. A
 *    money figure clipped to "12.5..." is a WRONG number read confidently, and
 *    a squeezed control is a button that has left the table.
 *  - `soft`: a state pill. Kept whole whenever the table can afford it, and
 *    released into the elastic pool when the only alternative is a sideways
 *    scroll. A clipped pill is a shorter word, not a wrong one - the whole of
 *    it is on the cell's `title` and a screen reader reads every character.
 *  - `elastic`: prose, which is what gives way first and always has.
 */
export type ColumnFit = 'hard' | 'soft' | 'elastic';

/** One column, reduced to the three facts the fit is decided on. */
export interface FitColumn {
  /** What the column takes when the browser lays the table out unfitted. */
  readonly natural: number;
  readonly fit: ColumnFit;
  /**
   * What this column's HEADING needs to stay a word: the first `NAMED_CHARS`
   * characters of it, plus the sort marks and the padding around them, measured
   * off the heading itself (`headingLeasts`).
   *
   * Optional, and absent means "no claim" rather than zero - a column whose
   * heading says nothing (a checkbox, a row menu) makes none, and neither does
   * a caller with no DOM to measure. `leastOf` is what reads it, and floors
   * every answer at `MIN_COLUMN_PX`.
   */
  readonly least?: number;
}

/**
 * The width for every column of a table that would overflow, or `undefined`
 * when there is nothing honest left to hand back and the wrapper should scroll
 * instead.
 *
 * Pure, and exported for that reason: this IS the decision, it is arithmetic
 * rather than layout, and until it was lifted out of `fitColumns` the only way
 * to ask it a question was to render a table in a browser and measure it.
 *
 * TWO ATTEMPTS, because the two tiers of `ColumnFit` are two rules that can
 * collide. The first holds the soft columns whole, which is what a reader
 * wants: a state pill drawn in full. If that cannot seat the table at all, the
 * second releases them into the elastic pool and tries again, which is the
 * founder's rule - a table fits the screen, whatever a screen puts in it - and
 * it is the one that wins, because it is the one stated as absolute.
 *
 * It wins only where it has to, which is why this is two attempts rather than
 * one policy. Measured 4 September 2026 in the 991px content column: internal
 * Devices, Dead letter, the Bakeries shop list and Releases all seat with their
 * pills whole and never reach the second attempt. Internal Properties does -
 * SIX of its eight cells are pills, so held whole the table wants 1277px, there
 * is nothing left to squeeze, and before this it simply scrolled sideways.
 * Released, its seven columns share 923 and it fits.
 *
 * ONE PASS OF SCALING IS NOT ENOUGH, because the floor and the budget are the
 * same pixels. Scaling every elastic column by `budget / elasticNatural` seats
 * them inside the budget exactly - and then a column that lands under
 * `MIN_COLUMN_PX` is clamped back up to it, and those added pixels come from
 * nowhere. The table overflows by precisely the amount the floor added, which
 * is the one thing this whole mechanism exists to prevent.
 *
 * Measured on the internal Activity feed in a 991px container (4 September
 * 2026). What (407, it holds the row's own button) and the row menu (68) keep
 * their width, leaving 516 for the five elastic columns; scaled, they came to
 * When 140, Where 79, Detail 233 - 452 between them - and Order and Actor, 64
 * between them. Those two were clamped to 56 each, so 64px of column was
 * charged out at 112, the table asked for 1039 in its 991, and the operator
 * got the sideways scrollbar: 48px of overflow, which is the 112 - 64 to the
 * pixel. Internal Devices, a row of counter telemetry, did the same thing. A
 * table whose elastic columns are all comfortably wide does not, which is how
 * this went out with forty-four converted tables behind it.
 *
 * So the floor is PAID FOR, out of the budget, by the columns still above it.
 * Each pass settles the columns that fell through the floor AT the floor,
 * subtracts what they now cost, and re-scales the rest against what is left.
 * Settling a column always costs more than its scaled share - that is what
 * falling through the floor means - so every pass leaves the others less and
 * can only push further columns down, never back up. It therefore terminates:
 * a pass either settles at least one column or is the last one, so there are
 * at most as many passes as there are elastic columns. On Activity that is two
 * passes: the three columns still above the floor share 404 rather than 452
 * (When 125, Where 70, Detail 208) and the table asks for 990 in its 991.
 *
 * `Math.floor` on every width is what makes the result fit rather than merely
 * come close: a pixel rounded up per column is a table that overflows by the
 * number of columns.
 *
 * AND THE FLOOR IS PER COLUMN, not one number for the table. A column settled at
 * 56px fits and says nothing: on internal Activity that was Order and Actor,
 * headings printing `C` and `A` over cells reading `V...` and `P...`. So each
 * column carries what its own HEADING needs to stay a word (`FitColumn.least`,
 * `leastOf`), the settling above reads that instead of the bare minimum, and the
 * pixels it costs come off the roomiest columns through the very same
 * redistribution - which is why this is one extra term rather than a second
 * mechanism. `MIN_COLUMN_PX` is what a column with no heading to speak of falls
 * back to, and nothing else.
 */
export function fitColumnWidths(
  columns: readonly FitColumn[],
  available: number,
): number[] | undefined {
  return nameWhatItCan(columns, available, true) ?? nameWhatItCan(columns, available, false);
}

/**
 * AS MANY HEADINGS AS THE WIDTH WILL PAY FOR, CHEAPEST FIRST - and never a
 * refusal to fit because it could not pay for them all.
 *
 * The heading minimum was a hard admission test for one commit: if the elastic
 * columns could not cover their headings between them, `seatColumns` returned
 * nothing and `.cib-tablewrap` scrolled. Measured on internal Activity that is
 * wrong twice over. At an 846px container the feed holds three HELD columns -
 * When (a date, 146), What (it carries the row's button, 294) and the row menu
 * (68) - so 508px is spoken for before any prose is seated, and the four elastic
 * headings want 390 of the 338 that is left. There is no set of widths that
 * names every column, and the answer the reader got was a table 2241px wide in
 * an 846px box: the same headings unreadable, now off-screen, and every value
 * with them. "A table fits the screen" is the rule stated as absolute
 * (`fitColumns`), and a heading floor is not allowed to overturn it.
 *
 * So the floors are GRADED. Seat once at the last-resort floor to find out which
 * columns are actually short of their heading - a column already seated above it
 * prints its name and needs nothing bought - then buy those headings in
 * ascending order of what they cost until the next one does not fit, and seat
 * again with what was bought. Cheapest first because the question is how many
 * columns can be NAMED, and a wide heading bought early is two narrow ones
 * unbought.
 *
 * Two properties come out of that, and both are the reason it is shaped this way
 * rather than as a third attempt beside `keepSoft`:
 *
 *  - the admission test goes back to being the last-resort floor alone, because
 *    the floors are chosen to fit the budget by construction. A heading can no
 *    longer make a table unfittable, only unnamed;
 *  - a table that can pay for every heading pays for every heading, so nothing
 *    measured before this changes.
 *
 * On Activity at 846 it buys Order (98) and Actor (99), runs out before Where
 * (101), and seats `146 | 294 | 98 | 56 | 99 | 85 | 68`. Two of the three
 * one-letter headings come back and Detail - prose, which clips with an ellipsis
 * and keeps the whole of itself on the cell's `title` - takes the squeeze. The
 * third is not affordable at that width by 16px, and the lever for it is the
 * HELD tier holding 508 of 846, not this rung.
 */
function nameWhatItCan(
  columns: readonly FitColumn[],
  available: number,
  keepSoft: boolean,
): number[] | undefined {
  const bare = columns.map(() => MIN_COLUMN_PX);
  const plain = seatColumns(columns, available, keepSoft, bare);
  // Not seatable at the last-resort floor either: there is nothing honest left,
  // and the wrapper scrolls as it always has.
  if (plain === undefined) return undefined;
  const floors = affordableFloors(columns, available, keepSoft, plain);
  return seatColumns(columns, available, keepSoft, floors) ?? plain;
}

/**
 * Which headings this width can pay for, given what a floor-only seating did.
 *
 * `plain` is that seating, and it is what says who is SHORT: a column already
 * drawn wider than its heading needs is naming itself for free. Everything else
 * is a purchase, priced at the pixels above the last-resort floor, and they are
 * made cheapest first until one does not fit - at which point the loop stops
 * rather than skipping ahead to a cheaper one further down, because the list is
 * already sorted and there is nothing cheaper further down.
 */
function affordableFloors(
  columns: readonly FitColumn[],
  available: number,
  keepSoft: boolean,
  plain: readonly number[],
): number[] {
  const floors = columns.map(() => MIN_COLUMN_PX);
  const elastic = columns.filter((column) => !isHeld(column, keepSoft));
  const budget =
    available - columns.reduce((sum, c) => sum + (isHeld(c, keepSoft) ? c.natural : 0), 0);

  let cost = elastic.length * MIN_COLUMN_PX;
  const wanting = columns
    .map((column, index) => ({ index, claim: leastOf(column) }))
    .filter(({ index, claim }) => {
      const column = columns[index];
      if (column === undefined || isHeld(column, keepSoft)) return false;
      return claim > MIN_COLUMN_PX && (plain[index] ?? 0) < claim;
    })
    .sort((left, right) => left.claim - right.claim);

  for (const { index, claim } of wanting) {
    const extra = claim - MIN_COLUMN_PX;
    if (cost + extra > budget) break;
    cost += extra;
    floors[index] = claim;
  }
  return floors;
}

/** Whether this attempt is holding this column's width rather than seating it. */
function isHeld(column: FitColumn, keepSoft: boolean): boolean {
  return column.fit === 'hard' || (keepSoft && column.fit === 'soft');
}

/**
 * WHY THERE IS NO CAP ON THE WIDEST COLUMN HERE ANY MORE.
 *
 * There was one for a day: no elastic column keeps more than 40% of the elastic
 * budget while another sits at the floor, the widest column pinned at that share
 * and handed back to `seatColumns` so the width it released flowed to the
 * starved ones. It was measured on internal Activity with 100 rows and it worked
 * exactly as designed - What came down from 408px to 38% of the budget in both a
 * 846px container and a 1038px one, and at 1038 nothing was left at the floor -
 * and the screen was no better: the headings still read `C`, `W...` and `A`,
 * because Order at 69px and Actor at 73px cannot print a five-letter name beside
 * their sort marks.
 *
 * A percentage cap is the wrong instrument, and the measurement is what says so.
 * It reasons about the WIDEST column, and the property that has to hold is a
 * property of the NARROWEST: a column may not be given less than its heading
 * needs to stay a word. That is the rule `releaseHeadingAir` below already
 * states for a held column - "A heading may be shortened; it may not stop naming
 * its column" - and it is now stated for every column, as `FitColumn.least`.
 *
 * With that in place the cap could not be kept even as a second line of defence,
 * because the heading floor redefines the word it triggered on. "At the floor"
 * used to mean "cut to 56px and unreadable"; it now means "seated at exactly the
 * width its own heading asked for", which is a column that is FINE. A valve
 * firing on that would file the widest column down on healthy tables, for
 * nothing. Two rules answering one question, and the newer one answers it in the
 * units the defect is actually stated in.
 */
/**
 * A HELD COLUMN MAY GIVE UP ITS HEADING'S AIR - THE ROOMIEST FIRST, AND ONLY AS
 * MUCH AS THE TABLE IS SHORT.
 *
 * The hard tier exists to protect a VALUE: a money figure clipped to "12.5..."
 * is a wrong number, read confidently. A heading is not a value - it is one
 * fixed word above every row - so a held column wider than its figures is
 * holding width on its heading's behalf, and until this the columns beside it
 * paid for that: prose gave up everything before a held column gave up
 * anything.
 *
 * Measured on internal Finance, 5 September 2026, in a 1308px container. The
 * payouts table wanted 1335; 158 of those pixels were heading air on four money
 * columns - ADJUSTMENTS asked 156px to print a figure that needs 80, ORDERS 108
 * to print `1` in 60 - and the 29px of overflow came off RECIPIENT, clipping all
 * sixteen names on the screen to make room for words like ADJUSTMENTS.
 *
 * TWO THINGS RATION IT, AND BOTH WERE PAID FOR ON THE SCREEN.
 *
 * Taking every pixel of that air is the obvious move and it is wrong: tried,
 * the same table came back with columns headed `O`, `A...` and `C...` - two
 * adjacent money columns reduced to a letter each, a worse table than the one
 * being fixed. A heading may be shortened; it may not stop naming its column.
 * So only the shortfall is taken - 29px of the 158, not the 158.
 *
 * And spreading those 29 evenly is wrong too, for the same reason one step down:
 * it cut four headings a little each (`ORD...`, `PAYAB...`, `CAR...`) when one
 * of them had 75px spare and the others had six. So it is taken from the
 * roomiest downwards - the water-filling `threshold` below, the same idea as the
 * seating rule that the width comes off the WIDEST columns - which on that table
 * takes essentially all 29 from ADJUSTMENTS and leaves ORDERS, PAYABLE and CARRY
 * with their headings whole.
 *
 * `SAFETY` is the last pixel, and it is not superstition: a column's body is
 * measured at `max-content` and lands a sub-pixel under what its cell then needs,
 * so a column with "1px of air" that gave that pixel up printed `Mon 24/...` -
 * a clipped DATE, which is the very thing the tier forbids. A column with
 * nothing but rounding to give now gives nothing.
 *
 * When the air runs out before the shortfall does, the old order resumes and the
 * elastic columns absorb the rest: by then the alternative is not a narrower
 * name but a table that scrolls sideways.
 */
export function releaseHeadingAir(
  columns: readonly FitColumn[],
  bodies: readonly (number | undefined)[] | undefined,
  available: number,
): FitColumn[] {
  const kept = columns.map((column) => ({ ...column }));
  if (bodies === undefined) return kept;

  const shortfall = columns.reduce((sum, column) => sum + column.natural, 0) - available;
  if (shortfall <= 0) return kept;

  /** The pixel a `max-content` measurement is allowed to be wrong by. */
  const SAFETY = 1;

  // What each held column is holding for its heading alone: the gap between the
  // width it was measured at and the width its own cells need - and never below
  // what its heading needs to stay a word (`leastOf`). "A heading may be
  // shortened; it may not stop naming its column" was stated above and not held
  // here: internal Orders at a 1200px window filed TYPE down to 57px, its label
  // to 7px - `T` beside its sort marks (#2004 WP5, 2026-10-08). A heading of five
  // characters or fewer is its own least, so it keeps every letter.
  const air = columns.map((column, index) => {
    if (column.fit === 'elastic') return 0;
    const body = bodies[index];
    if (body === undefined) return 0;
    return Math.max(0, column.natural - Math.max(body, leastOf(column)) - SAFETY);
  });
  const total = air.reduce((sum, one) => sum + one, 0);
  if (total <= 0) return kept;

  // The level the roomiest columns are filed down to. Everything above it is
  // taken and everything at or below it is untouched, so a column with six
  // pixels spare keeps its heading while the one with seventy-five pays.
  const wanted = Math.min(shortfall, total);
  let threshold = 0;
  let ceiling = Math.max(...air);
  // Thirty halvings settle a pixel budget to far finer than a pixel, and the
  // loop is bounded rather than converging on a float comparison.
  for (let step = 0; step < 30; step += 1) {
    const mid = (threshold + ceiling) / 2;
    const taken = air.reduce((sum, one) => sum + Math.max(0, one - mid), 0);
    // Keep the highest level that still frees enough; a level that frees too
    // little is the one to walk back from.
    if (taken >= wanted) threshold = mid;
    else ceiling = mid;
  }

  return kept.map((column, index) => ({
    ...column,
    natural: column.natural - Math.max(0, (air[index] ?? 0) - threshold),
  }));
}

/**
 * One attempt, with the soft columns on the side `keepSoft` puts them.
 *
 * Both attempts are the same arithmetic over a different set of inelastic
 * columns, which is deliberate: the retry is a change of INPUT, not a second
 * algorithm, so there is one thing to reason about and one thing under test.
 */
function seatColumns(
  columns: readonly FitColumn[],
  available: number,
  keepSoft: boolean,
  floors: readonly number[],
): number[] | undefined {
  const held = (column: FitColumn): boolean => isHeld(column, keepSoft);
  const floorAt = (index: number): number => floors[index] ?? MIN_COLUMN_PX;
  const fixedTotal = columns.reduce((sum, c) => sum + (held(c) ? c.natural : 0), 0);
  const elasticCount = columns.reduce((count, c) => count + (held(c) ? 0 : 1), 0);
  const budget = available - fixedTotal;
  // What the elastic columns need between them: their own floors, summed, not a
  // count times one number. `affordableFloors` chooses those floors so that this
  // sum fits the budget wherever the last-resort floor would have, so a heading
  // can leave a column unnamed but can no longer make a table unfittable.
  const floorTotal = columns.reduce((sum, c, index) => sum + (held(c) ? 0 : floorAt(index)), 0);
  // Every column is inelastic, or what is left will not seat them: there is
  // nothing honest left to squeeze, and `.cib-tablewrap` scrolls as the safety
  // net it has always been rather than shredding a column of figures.
  if (elasticCount === 0 || budget < floorTotal) return undefined;

  const atFloor = columns.map(() => false);
  let scale = 0;
  for (;;) {
    let freeNatural = 0;
    let settledCost = 0;
    let settledCount = 0;
    columns.forEach((column, index) => {
      if (held(column)) return;
      if (atFloor[index] === true) {
        settledCount += 1;
        settledCost += floorAt(index);
      } else freeNatural += column.natural;
    });
    // Nothing left to scale: every elastic column is at its floor, and the
    // guard above already proved the budget seats them all there.
    if (settledCount === elasticCount) break;
    const freeBudget = budget - settledCost;
    // A column asking for nothing cannot be scaled into something. It falls to
    // the floor with the rest of this pass rather than dividing by zero.
    scale = freeNatural > 0 ? freeBudget / freeNatural : 0;
    let settledOne = false;
    columns.forEach((column, index) => {
      if (held(column) || atFloor[index] === true) return;
      if (Math.floor(column.natural * scale) < floorAt(index)) {
        atFloor[index] = true;
        settledOne = true;
      }
    });
    if (!settledOne) break;
  }

  const widths = columns.map((column, index) =>
    held(column)
      ? column.natural
      : atFloor[index] === true
        ? floorAt(index)
        : Math.floor(column.natural * scale),
  );

  // The promise, asserted rather than argued. The passes above cannot exceed
  // the budget - the free columns are handed at most what the settled ones left
  // and every width is floored - but "cannot exceed the budget" is exactly what
  // was believed of the single pass this replaced. Over it, the table is left
  // alone and scrolls, which is the same honest answer as the guard's.
  const total = widths.reduce((sum, width) => sum + width, 0);
  if (total > available + 1) return undefined;
  return widths;
}

/**
 * What one cell says about its column's tier.
 *
 * Read off `--cib-cell-whole`, which layout.css sets from ONE selector list per
 * tier - so "this cell keeps its size" is defined in the stylesheet and never
 * in a second copy of those selectors here. An unset property, or any value
 * neither rule wrote, is an ordinary elastic cell.
 */
function cellFit(cell: Element): ColumnFit {
  const declared = getComputedStyle(cell).getPropertyValue('--cib-cell-whole').trim();
  if (declared === '1') return 'hard';
  if (declared === 'soft') return 'soft';
  return 'elastic';
}

const FIT_RANK: Readonly<Record<ColumnFit, number>> = { elastic: 0, soft: 1, hard: 2 };

/** The stronger claim of two cells in one column. */
function strongerFit(a: ColumnFit, b: ColumnFit): ColumnFit {
  return FIT_RANK[a] >= FIT_RANK[b] ? a : b;
}

/**
 * A TABLE FITS THE SCREEN (founder, 3 September 2026).
 *
 * The rule is absolute and the mechanism cannot be, which is the whole reason
 * this is here rather than in a stylesheet. A CSS cap that always applies has
 * to apply to every column, and then a table that already fitted comes out
 * flattened to equal columns - measured on the Clients list, where the client's
 * NAME was clipped while State kept half a column of air. So the decision is
 * made per table, against the container it is actually in:
 *
 *  - a table that fits is not touched at all, and keeps the column widths its
 *    content negotiated;
 *  - a table that would overflow is pinned to `table-layout: fixed` at widths
 *    proportional to what each column asked for, so the width comes off the
 *    widest columns rather than off all of them equally;
 *  - a column that cannot give anything up - money, a date, a control, anything
 *    marked `--cib-cell-whole: 1` in layout.css - keeps what it asked for, and
 *    the prose columns beside it absorb the difference;
 *  - a column that would rather not - a state pill, `--cib-cell-whole: soft` -
 *    keeps it too, unless holding it whole is what makes the table unfittable.
 *    See `ColumnFit`.
 *
 * Cells clip with an ellipsis on their own (layout.css) and `titleClippedCells`
 * hands back whatever was clipped, so fitting a table loses nothing.
 *
 * What is left here is the measuring and the writing. The proportions - and the
 * floor under them, which is what a single pass of scaling gets wrong - are
 * `fitColumnWidths` above, where they can be checked without a browser.
 */
function fitColumns(table: HTMLTableElement, columnCount: number): void {
  /*
   * THE FIT COMES OFF FIRST, AND EVERY PATH OUT OF HERE LEAVES IT OFF UNLESS IT
   * WRITES A NEW ONE.
   *
   * It has to come off anyway to measure what the table WANTS - a fitted table
   * measures the widths it was given, not the ones it needs - so this is one
   * line moved rather than a line added. What it buys is that a "cannot fit" is
   * no longer a return PAST a fit that already existed.
   *
   * That was a live defect, and the shape of it is worth keeping: collapsing the
   * sidebar fitted the Activity feed at 1038 and expanding it back left the
   * colgroup in place, holding the 1038 widths inside an 846 container, so the
   * table scrolled sideways until a reload - while a FRESH load at the same 846
   * scrolled with no colgroup at all. Same width, two different states. The
   * pixels came off the guard below that returns on a zero-width container,
   * which is what a box mid-transition reads as, and which was the one path here
   * that returned without releasing. A fit for a width that no longer exists is
   * worse than no fit: it hides the honest scrollbar behind a wrong one.
   *
   * Reading after this write is one forced layout per table per render, and that
   * is the price of the rule.
   */
  releaseFit(table);

  // Card mode is not a set of columns at all - every cell is a block on a
  // 390px screen - so there is nothing to fit. Detected from what the cell
  // actually computes to rather than from a breakpoint repeated here.
  const probe = table.tBodies[0]?.rows[0]?.cells[0];
  if (probe !== undefined && getComputedStyle(probe).display === 'block') return;
  const available = table.parentElement?.clientWidth ?? 0;
  if (columnCount === 0 || available === 0) return;

  const head = table.tHead;
  const headRow = head?.rows[head.rows.length - 1];
  // A header that spans columns cannot be measured cell for cell; leave the
  // table to the browser rather than fit it against the wrong count.
  if (headRow === undefined || headRow.cells.length !== columnCount) return;

  const natural = table.scrollWidth;
  if (natural <= available + 1) return;

  const leasts = headingLeasts(table, headRow);
  const columns = Array.from(headRow.cells).map((cell, index) => ({
    natural: cell.getBoundingClientRect().width,
    fit: cellFit(cell),
    // What this heading needs to stay a word, whatever the prose beside it
    // wants. Measured on the heading itself, above, and never more than the
    // column's own natural - see `leastOf`.
    least: leasts[index] ?? 0,
  }));
  // A column is inelastic if its heading says so or if any cell under it does -
  // the money and the controls are in the body, not in the header row. The
  // STRONGER of the two wins: a heading that carries no pill and a body cell
  // that does make a soft column, and a body cell holding a control makes a
  // hard one whatever sits in the heading above it.
  const firstRow = table.tBodies[0]?.rows[0];
  if (firstRow !== undefined && firstRow.cells.length === columnCount) {
    Array.from(firstRow.cells).forEach((cell, index) => {
      const column = columns[index];
      if (column === undefined) return;
      column.fit = strongerFit(column.fit, cellFit(cell));
    });
  }

  // ...and what they would take with no heading over them, which is the air a
  // held column may be asked for before any prose is squeezed. See
  // `releaseHeadingAir`, which rations it.
  const asked = releaseHeadingAir(columns, bodyNaturals(table, columnCount), available);

  // The decision itself is `fitColumnWidths`, above: measuring and writing is
  // all that has to happen against a real DOM, and the arithmetic that was
  // wrong is now the part a unit test can ask a question of.
  const widths = fitColumnWidths(asked, available);
  if (widths === undefined) return;

  const group = table.ownerDocument.createElement('colgroup');
  group.setAttribute(FIT_MARK, '');
  for (const width of widths) {
    const col = table.ownerDocument.createElement('col');
    col.style.width = `${width}px`;
    group.append(col);
  }
  table.prepend(group);
  table.style.tableLayout = 'fixed';
}

/**
 * What each column would take if its heading were not there.
 *
 * Two things are turned off to ask it. The heading row, obviously - and the
 * table's own `width: 100%`, because a table told to fill its box hands the
 * slack out to the columns and the answer comes back inflated by whatever was
 * left over. `max-content` asks each column for its intrinsic width instead,
 * which is the only number that means "this is what the data needs".
 *
 * Written and read inside one frame, like the release-and-measure pass above
 * it, so nothing is ever painted in this state.
 *
 * `undefined` when the body has no row this can be read off - an empty table,
 * or one whose every row spans columns. A column with no answer keeps its
 * heading's width, which is what it had before any of this.
 */
function bodyNaturals(table: HTMLTableElement, columnCount: number): number[] | undefined {
  const head = table.tHead;
  if (head === null) return undefined;
  const row = Array.from(table.tBodies)
    .flatMap((body) => Array.from(body.rows))
    .find((candidate) => candidate.cells.length === columnCount);
  if (row === undefined) return undefined;

  const headDisplay = head.style.display;
  const tableWidth = table.style.width;
  head.style.display = 'none';
  table.style.width = 'max-content';
  const widths = Array.from(row.cells).map((cell) => cell.getBoundingClientRect().width);
  head.style.display = headDisplay;
  table.style.width = tableWidth;
  return widths;
}

/**
 * WHAT EACH HEADING NEEDS TO STAY A WORD - the mirror of `bodyNaturals`, and the
 * one number that stops a fitted column printing a letter.
 *
 * Measured the same way, from the other side: the BODY is hidden rather than the
 * head, and the table is asked for `max-content`, so each heading answers with
 * what it alone wants - the words, the sort marks, and the padding around them.
 * A column's `natural` is the wider of its heading and its values, so this is
 * never the same question: `Order` measures 107 in the Activity feed because the
 * codes under it are wide, and its heading wants 86.
 *
 * The words are then cut to `NAMED_CHARS`, and cut BY MEASUREMENT rather than by
 * a pixels-per-character constant: `.cib-sort__label` is a box holding the
 * heading text and nothing else (see `SortHeader`, which put it in a span for a
 * neighbouring reason), so the width of one character in this font at this size
 * is that box over its own length, and everything outside it - the marks, the
 * padding - is charged whole. A heading already at or under five characters
 * keeps all of itself, which is most of them.
 *
 * A column whose heading says nothing - a checkbox, a row menu, anything
 * `cib-visually-hidden` - measures a padding's worth and floors at
 * `MIN_COLUMN_PX` like everything else, with no special case needed: there is no
 * word to protect, so nothing is claimed.
 */
function headingLeasts(table: HTMLTableElement, headRow: HTMLTableRowElement): number[] {
  const sections = [...Array.from(table.tBodies), ...(table.tFoot === null ? [] : [table.tFoot])];
  const displays = sections.map((section) => section.style.display);
  const tableWidth = table.style.width;
  for (const section of sections) section.style.display = 'none';
  table.style.width = 'max-content';

  const leasts = Array.from(headRow.cells).map((cell) => {
    const whole = cell.getBoundingClientRect().width;
    const label = cell.querySelector('.cib-sort__label');
    const text = (cell.textContent ?? '').replace(/\s+/gu, ' ').trim();
    if (text.length <= NAMED_CHARS) return Math.ceil(whole);
    // Five characters AND the ellipsis that follows them: a box sized for five
    // letters alone prints four and the ellipsis, which on internal Orders at a
    // 1200px window was `BAK...` (#2004 WP5, 2026-10-08). Never past the whole
    // heading - a six-letter word is then simply kept.
    const shownChars = Math.min(text.length, NAMED_CHARS + 1);
    if (label === null) {
      // No label box to measure a character against - a plain heading with the
      // words as a bare text node. Its chrome is its own padding, which is the
      // one part of it `getComputedStyle` can still name.
      const style = getComputedStyle(cell);
      const pad = Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight);
      const words = Math.max(0, whole - (Number.isFinite(pad) ? pad : 0));
      return Math.ceil(whole - words + (words * shownChars) / text.length);
    }
    const words = label.getBoundingClientRect().width;
    return Math.ceil(whole - words + (words * shownChars) / text.length);
  });

  sections.forEach((section, index) => {
    section.style.display = displays[index] ?? '';
  });
  table.style.width = tableWidth;
  return leasts;
}

/** Puts the table back the way the browser would have laid it out. */
function releaseFit(table: HTMLTableElement): void {
  table.style.tableLayout = '';
  for (const group of Array.from(table.querySelectorAll(`colgroup[${FIT_MARK}]`))) {
    group.remove();
  }
}

/** Marks the titles this file owns, so a screen's own `title` is never taken. */
const AUTO_TITLE = 'autoTitle';

/**
 * Gives a clipped cell the whole of its value on `title`.
 *
 * A table fits the screen (layout.css, `.cib-table th, td`), which means a
 * value with nowhere left to go is cut with an ellipsis. Nothing is lost by
 * that - the text is still in the DOM and a screen reader reads all of it -
 * but the eye needs a way back to it, and hovering is that way.
 *
 * Only actually-clipped cells get one. A `title` on every cell would put a
 * tooltip over a table an operator is reading down, which is worse than the
 * ellipsis it explains; and the attribute is given back the moment a wider
 * window, a collapsed sidebar or a shorter value makes the cell whole again.
 */
function titleClippedCells(cells: readonly HTMLTableCellElement[]): void {
  for (const cell of cells) {
    // A cell overflows in whichever box actually holds the text, which is often
    // not the cell: a stacked cell clips in its two children, and a sortable
    // heading clips in the label span inside its button. Descendants, not just
    // children, and it stops at the first one that is cut.
    //
    // A `.cib-clamp` box (layout.css) wraps and is cut on the BLOCK axis
    // instead, after its last allowed line, so it is asked about its height as
    // well. Only that class: a box with no clamp that is taller than its client
    // area is not hiding anything from the reader.
    const clipped =
      cell.scrollWidth > cell.clientWidth + 1 ||
      Array.from(cell.querySelectorAll('*')).some(
        (box) =>
          box.scrollWidth > box.clientWidth + 1 ||
          (box.classList.contains('cib-clamp') && box.scrollHeight > box.clientHeight + 1),
      );
    const owned = cell.dataset[AUTO_TITLE] !== undefined;
    if (!clipped) {
      if (owned) {
        cell.removeAttribute('title');
        delete cell.dataset[AUTO_TITLE];
      }
      continue;
    }
    // A `title` the screen set itself says something the text does not - the
    // reason a payout is blocked, say - and is never replaced by the text.
    if (cell.hasAttribute('title') && !owned) continue;
    const text = (cell.textContent ?? '').replace(/\s+/gu, ' ').trim();
    if (text === '') continue;
    cell.setAttribute('title', text);
    cell.dataset[AUTO_TITLE] = '';
  }
}

// ---------------------------------------------------------------------------
// The component
// ---------------------------------------------------------------------------

export interface TableFrameProps {
  /** `<thead>` and `<tbody>`, written by the screen. */
  readonly children: ReactNode;
  /** Accessible name for the scrollable region and the table. Already translated. */
  readonly label: string;
  /**
   * Take the rest of the screen and own the only vertical scrollport, which is
   * what makes the sticky header actually pin (`.cib-tablewrap--fill`). The
   * screen root must be `.cib-page--fill` for this to have anything to fill.
   * Below `md` the stylesheet drops it: on a phone the page scrolls and the
   * table does not, or a screen's own header and filters squeeze the rows into
   * the last 60px of the viewport.
   */
  readonly fill?: boolean;
  /** Tighter padding for a dense operational list. */
  readonly compact?: boolean;
  /**
   * Below `md`, lay each row out as a card instead of scrolling sideways. On by
   * default. Turn it off for a genuinely matrix-shaped table, where the grid
   * itself is the information.
   */
  readonly cards?: boolean;
}

export function TableFrame({
  children,
  label,
  fill = false,
  compact = false,
  cards = true,
}: TableFrameProps): JSX.Element {
  const table = useRef<HTMLTableElement>(null);
  useCardLabels(table);

  return (
    // Focusable on purpose. A scrollable container that cannot take focus cannot
    // be scrolled from the keyboard - WCAG 2.1 SC 2.1.1 for exactly this pattern,
    // a wide table on a narrow screen. Labelled so a screen reader announces what
    // is being entered.
    <section
      className={cx(
        'cib-tablewrap',
        cards && 'cib-tablewrap--cards',
        fill && 'cib-tablewrap--fill',
      )}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: keyboard-scrollable region.
      tabIndex={0}
      aria-label={label}
    >
      <table ref={table} className={cx('cib-table', compact && 'cib-table--compact')}>
        {children}
      </table>
    </section>
  );
}
